<!-- ----------------------------------------- template ----------------------------------------- -->
<template>
  <div>
    <div class="instruction-box">
      <h3>Club Bulletin Board</h3>
      <p>
        This is your club's internal bulletin board for posting notices, news, and announcements.
        All posts are visible to club members within the eClub system. Use this space to share updates,
        coordinate activities, and keep everyone informed about club happenings.
      </p>
    </div>

    <div class="discussions-container">
      <div class="discussions-list" :class="{ 'with-detail': selectedPostId }">
      <div style="display: flex; justify-content: flex-end; margin-bottom: 16px;">
        <el-button type="primary" @click="openNewDiscussionDialog">
          New Post
        </el-button>
      </div>

      <template v-if="discussions && discussions.length > 0">
        <div v-for="discussion in discussions" :key="discussion.id" class="discussion-item" style="background: white; border-radius: 8px; padding: 16px; margin-bottom: 16px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);" @click="handleDiscussionClick(discussion)">
          <h3 :style="{ color: discussion._id.$id === selectedPostId ? '#409EFF' : 'inherit' }">{{ discussion.title }}</h3>
          <p>{{ discussion.content }}</p>
          <div class="discussion-meta">
            <span><i class="el-icon-chat-line-square"></i> {{ discussion.msg_count }}</span>
            <span><i class="el-icon-user"></i> {{ formatName(discussion.creator_name) }}</span>
            <span>{{ formatDate(discussion.create_time) }}</span>
          </div>
        </div>
      </template>
      <empty-state v-else message="No posts yet" description="Be the first to start a discussion in this club!"></empty-state>
    </div>

    <div v-if="selectedPostId" class="discussion-detail">
      <el-button class="close-button" type="text" @click="selectedPostId = null">
        <i class="el-icon-close"></i>
      </el-button>
      
      <div class="detail-content">
        <h2>{{ selectedDiscussion?.title }}</h2>
        <p>{{ selectedDiscussion?.content }}</p>
        <div class="discussion-meta">
          <span><i class="el-icon-user"></i> {{ formatName(selectedDiscussion?.creator_name) }}</span>
          <span>{{ formatDate(selectedDiscussion?.create_time) }}</span>
        </div>
      </div>
      <div class="messages-section">
        <h3>Comments</h3>
        <div class="messages-list">
          <div v-if="messages && messages.length > 0">
            <div v-for="message in messages" :key="message._id.$id" class="message-item">
              <div class="message-meta">
                <span class="message-author">{{ formatName(message.user_name) }}</span>
                <span class="message-time">{{ formatDate(message.create_time) }}</span>
              </div>
              <div class="message-content">
                {{ message.content }}
              </div>
            </div>
          </div>
          <div v-else class="no-messages">
            No comments yet. Be the first to comment!
          </div>
        </div>
        <div class="new-comment">
          <div v-if="selectedDiscussion.message_status === 'open'">
            <el-input
              type="textarea"
              v-model="newComment"
              :rows="3"
              placeholder="Write a comment..."
            ></el-input>
            <div style="text-align: right; margin-top: 10px;">
              <el-button type="primary" size="small" @click="submitComment">Comment</el-button>
            </div>
          </div>
          <div v-else class="closed-message" style="text-align: center; padding: 10px; color: #909399;">
            <i class="el-icon-lock" style="margin-right: 5px;"></i>
            Comment function for this discussion is closed
          </div>
        </div>
      </div>
    </div>
    </div>

    <!-- New Post Dialog -->
    <el-dialog
      title="New Post"
      :visible.sync="dialogVisible"
      width="50%"
      @close="handleDialogClose"
    >
      <el-form :model="newPost" :rules="rules" ref="postForm" label-width="100px">
        <el-form-item label="Title" prop="title">
          <el-input v-model="newPost.title" placeholder="Enter discussion title"></el-input>
        </el-form-item>

        <el-form-item label="Content" prop="content">
          <el-input
            type="textarea"
            v-model="newPost.content"
            :rows="6"
            placeholder="Enter discussion content"
          ></el-input>
        </el-form-item>

        <el-form-item label="Attachments">
          <el-upload
            class="upload-demo"
            action="#"
            :auto-upload="false"
            :on-change="handleFileChange"
            :file-list="fileList"
            multiple
          >
            <el-button size="small" type="primary">Click to upload</el-button>
            <div slot="tip" class="el-upload__tip">Select files to attach</div>
          </el-upload>
        </el-form-item>
      </el-form>

      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">Cancel</el-button>
        <el-button type="primary" @click="submitPost">Create</el-button>
      </span>
    </el-dialog>

  </div>
</template>

<!-- ------------------------------------------ script ------------------------------------------ -->
<script>
import { postRequest } from '@/api/eclub'
import { getUserId, getUserType } from '@/utils/store'
import { formatDate } from '@/utils/date'
import EmptyState from '@/components/EmptyState.vue'

export default {
  name: '',
  components: {
    EmptyState
  },
  props: {
    clubInfo: {
      type: Object,
      required: true
    },
    club_id: {
      type: [String, Number],
      required: true
    }
  },
  data () {
    return {
      discussions: [],
      isLoading: false,
      error: null,
      dialogVisible: false,
      selectedPostId: null,
      newPost: {
        title: '',
        content: '',
        attachements: [],
        club_id: '',
        user_id: '',
        id: '' // empty for new posts
      },
      rules: {
        title: [
          { required: true, message: 'Please enter a title', trigger: 'blur' },
          { min: 3, max: 100, message: 'Length should be 3 to 100 characters', trigger: 'blur' }
        ],
        content: [
          { required: true, message: 'Please enter content', trigger: 'blur' },
          { validator: (rule, value, callback) => {
            if (!value || value.trim().length < 10) {
              callback(new Error('Content should be at least 10 characters'))
            } else {
              callback()
            }
          }, trigger: 'blur' }
        ]
      },
      fileList: [],
      selectedDiscussion: null,
      messages: [],
      newComment: '',
    }
  },

  /* 一.生命周期函数 */
  mounted () {
    console.log('club_id value upon creation:', this.club_id)
    this.fetchDiscussions()
  },

  /* 二.监控函数 */
  watch: {
  },

  computed: {
    canCreateDiscussion () {
      return this.clubInfo.userRole === 'member' || this.clubInfo.userRole === 'leader' || this.clubInfo.userRole === 'admin'
    },
    formatName() {
      return (name) => {
        if (!name) return '';
        return name.split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      }
    }
  },

  /* 三.内部功能函数 */
  methods: {
    /* ----------------------事件调用函数------------------------ */
    openNewDiscussionDialog () {
      this.dialogVisible = true
      this.newPost = {
        title: '',
        content: '',
        attachements: [],
        club_id: this.club_id,
        user_id: getUserId(),
        user_type: getUserType(),
        id: ''
      }
    },

    handleFileChange (file, fileList) {
      this.fileList = fileList
      // You might want to process the files here, perhaps upload them to get URLs
      // For now we'll just store the file names
      this.newPost.attachements = fileList.map(f => f.name)
    },

    handleDiscussionClick (discussion) {
      if (!this.selectedPostId) {
        this.selectedPostId = discussion._id.$id
        this.selectedDiscussion = discussion
        this.fetchMessages(discussion._id.$id)
      }else{
        this.selectedPostId = null
      }
    },

    async submitPost () {
      try {
        await this.$refs.postForm.validate()
        console.log('newPost:', this.newPost)
        const response = await postRequest('uploadClubPost', this.newPost)
        
        if (response.data.code === 0) {
          this.$message.success('Discussion created successfully')
          this.dialogVisible = false
          this.fetchDiscussions() // Refresh the discussions list
        } else {
          this.$message.error(response.data.message || 'Failed to create discussion')
        }
      } catch (error) {
        console.error('Error creating discussion:', error)
        this.$message.error('Failed to create discussion')
      }
    },

    /* ----------------------服务请求函数------------------------ */
    async fetchDiscussions () {
      try {
        this.isLoading = true
        console.log('club_id value:', this.club_id)
        const response = await postRequest('getClubPosts', {
          club_id: this.club_id
        })
        this.discussions = response.data.data
      } catch (err) {
        console.error('Error details:', err)
        this.error = err.message
      } finally {
        this.isLoading = false
      }
    },

    formatDate(unixtime) {
      if (!unixtime) return ''
      const date = new Date(unixtime*1000)
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    handleDialogClose () {
      this.$refs.postForm?.resetFields()
      this.fileList = []
      this.dialogVisible = false
    },

    async fetchMessages(from_id) {
      try {
        const response = await postRequest('getMessages', {
          from_id: from_id,
          type: 'post'
        })
        
        if (response.data.code === 0) {
          // Store messages in component data
          this.messages = response.data.data
          // Each message in the array will have:
          // - message content
          // - create_time
          // - user object with student details
        } else {
          this.$message.error('Failed to fetch messages')
        }
      } catch (error) {
        console.error('Error fetching messages:', error)
        this.$message.error('Failed to fetch messages')
      }
    },

    async submitComment() {
      if (!this.newComment.trim()) {
        this.$message.warning('Please enter a comment')
        return
      }

      try {
        const response = await postRequest('sendMessage', {
          from_id: this.selectedPostId,
          type: 'post',
          user_id: getUserId(),
          user_type: getUserType(),
          content: this.newComment,
          attachement: [] // Optional: Add attachment handling if needed
        })

        if (response.data.code === 0) {
          this.$message.success('Comment added successfully')
          this.newComment = '' // Clear the input
          await this.fetchMessages(this.selectedPostId) // Refresh messages
        } else {
          this.$message.error(response.data.message || 'Failed to add comment')
        }
      } catch (error) {
        console.error('Error adding comment:', error)
        this.$message.error('Failed to add comment')
      }
    }
  }
}
</script>

<!-- ------------------------------------------ style ------------------------------------------ -->
<style scoped lang="scss">
.instruction-box {
  background-color: #f5f7fa;
  border-radius: 8px;
  padding: 16px 20px;
  margin-bottom: 24px;

  h3 {
    color: #303133;
    margin: 0 0 8px 0;
    font-size: 16px;
  }

  p {
    color: #606266;
    margin: 0;
    font-size: 14px;
    line-height: 1.6;
  }
}

.discussions-container {
  padding: 20px;
  position: relative;
  display: flex;
  gap: 20px;
  min-width: 1200px;
}

.discussions-list {
  width: 600px;
  flex-shrink: 0;

  &.with-detail {
    width: 600px;
  }
}

.discussion-detail {
  width: 50%;
  background: white;
  border-left: 1px solid #eee;
  padding: 20px;
  overflow-y: auto;
  box-shadow: -2px 0 4px rgba(0,0,0,0.1);
  display: flex;
  flex-direction: column;
  height: calc(100vh - 120px);
  position: sticky;
  top: 20px;

  .detail-content {
    max-width: 800px;
    margin: 0 auto;

    h2 {
      margin-top: 0;
    }

    .discussion-meta {
      font-size: 0.9em;
      color: #666;
      margin-top: 10px;

      span {
        margin-right: 15px;
        &:last-child {
          color: #999;
        }
      }
    }
  }
}

.discussion-item {
  border-bottom: 1px solid #eee;
  padding: 15px 0;
  width: 100%;
  box-sizing: border-box;

  h3 {
    margin: 0 0 10px 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
  }

  p {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .discussion-meta {
    font-size: 0.9em;
    color: #666;
    margin-top: 10px;

    span {
      margin-right: 15px;
      &:last-child {
        color: #999;
      }
    }
  }
}

.el-upload__tip {
  font-size: 12px;
  color: #606266;
  margin-top: 7px;
}

.dialog-footer {
  text-align: right;
  display: block;
}

.close-button {
  position: absolute;
  top: 10px;
  right: 10px;
}

.messages-section {
  margin-top: 30px;
  
  h3 {
    margin-bottom: 20px;
    padding-bottom: 10px;
    border-bottom: 1px solid #eee;
  }
}

.messages-list {
  max-height: 400px;
  overflow-y: auto;
  margin-bottom: 20px;
}

.message-item {
  padding: 15px;
  margin-bottom: 15px;
  background: #f9f9f9;
  border-radius: 8px;
  
  &:last-child {
    margin-bottom: 0;
  }
}

.message-meta {
  margin-bottom: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  .message-author {
    font-weight: 600;
    color: #333;
  }
  
  .message-time {
    font-size: 0.85em;
    color: #999;
  }
}

.message-content {
  color: #666;
  line-height: 1.5;
}

.no-messages {
  text-align: center;
  padding: 30px;
  color: #909399;
  font-style: italic;
}

.new-comment {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #eee;
}
</style>